import { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/react';
import {
  useGetMe,
  useUpdateMe,
  useGetSubscription,
  useGetMyReferrals,
  useListIntegrations,
  useConnectIntegration,
  useDeleteIntegration,
} from '@workspace/api-client-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { TierBadge } from '@/components/tier-badge';
import { Spinner } from '@/components/ui/spinner';
import { UserCircle, Zap, Link as LinkIcon, Trash2, CheckCircle2, ExternalLink, AlertTriangle, Bell, Gift, Users } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { getStoredUtmParams } from '@/hooks/use-utm';
import { trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const { user: clerkUser } = useUser();
  const { data: user } = useGetMe();
  const { data: subscription } = useGetSubscription();
  const { data: referrals } = useGetMyReferrals();
  const { data: integrations } = useListIntegrations();
  const { toast } = useToast();
  const updateMe = useUpdateMe();
  const connectIntegration = useConnectIntegration();
  const deleteIntegration = useDeleteIntegration();
  const [, setLocation] = useLocation();

  const [displayName, setDisplayName] = useState('');
  // undefined = not yet loaded; avoids showing wrong state before user data arrives
  const [emailNotifEnabled, setEmailNotifEnabled] = useState<boolean | undefined>(undefined);
  const [emailNotifLoading, setEmailNotifLoading] = useState(false);

  // Sync email notification preference from loaded user data
  useEffect(() => {
    if (user) setEmailNotifEnabled((user as any).emailNotificationsEnabled ?? true);
  }, [user]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirmingUpgrade, setConfirmingUpgrade] = useState(false);
  const [upgradeConfirmed, setUpgradeConfirmed] = useState(false);
  const [upgradePending, setUpgradePending] = useState(false);
  const upgradeCompletionTracked = useRef(false);

  // Capture once on mount — reading directly from window.location.search on every render
  // would cause upgradeReturn/checkoutParam to flip to false/null as soon as
  // history.replaceState cleans the URL, which would re-trigger the effect cleanup
  // and kill the polling interval before it completes.
  const [upgradeReturn] = useState(
    () => new URLSearchParams(window.location.search).get('upgrade') === 'success'
  );
  const [checkoutParam] = useState(
    () => new URLSearchParams(window.location.search).get('checkout') as 'starter' | 'pro' | null
  );

  useEffect(() => {
    if (!checkoutParam) return;
    // Clean the query string immediately to avoid re-triggering on back navigation.
    // This must happen even for an unrecognized value so a stray/invalid
    // ?checkout=... param never lingers in the URL bar.
    window.history.replaceState({}, '', window.location.pathname);
    if (checkoutParam !== 'starter' && checkoutParam !== 'pro') return;
    // Only initiate if the user isn't already on this tier or higher
    const currentTier = subscription?.tier;
    if (
      (checkoutParam === 'pro' && currentTier === 'pro') ||
      (checkoutParam === 'starter' && (currentTier === 'starter' || currentTier === 'pro'))
    ) {
      // Already on requested tier — just stay on settings
      return;
    }
    handleUpgrade(checkoutParam);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutParam]);

  // After returning from Stripe, poll subscription until tier === 'pro' (max ~30s)
  useEffect(() => {
    if (!upgradeReturn) return;

    // Clean up query string immediately
    window.history.replaceState({}, '', window.location.pathname);

    // If already upgraded (e.g. fast webhook), skip polling
    if (subscription?.tier !== 'free' && subscription?.tier !== undefined) {
      setUpgradeConfirmed(true);
      return;
    }

    setConfirmingUpgrade(true);

    let attempts = 0;
    const MAX_ATTEMPTS = 15; // ~30s at 2s intervals

    const poll = async () => {
      attempts++;
      await queryClient.invalidateQueries({ queryKey: ['/api/subscription'] });
      // React Query will re-fetch; we check via the subscription data in the next effect
    };

    const interval = setInterval(() => {
      poll();
      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(interval);
        setConfirmingUpgrade(false);
        setUpgradePending(true);
      }
    }, 2000);

    poll(); // immediate first check

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgradeReturn]);

  // Stop polling as soon as subscription data shows any paid tier.
  // Also clear the "still pending" banner if a later invalidation picks up the change.
  useEffect(() => {
    if (subscription?.tier !== 'free' && subscription?.tier !== undefined) {
      if (upgradeReturn && !upgradeCompletionTracked.current) {
        upgradeCompletionTracked.current = true;
        trackEvent(FUNNEL_EVENTS.UPGRADE_COMPLETED, {
          tier: subscription.tier,
          source: 'settings',
        });
      }
      if (confirmingUpgrade) {
        setConfirmingUpgrade(false);
        setUpgradeConfirmed(true);
        queryClient.invalidateQueries({ queryKey: ['/api/users/me'] });
      } else if (upgradePending) {
        setUpgradePending(false);
        setUpgradeConfirmed(true);
        queryClient.invalidateQueries({ queryKey: ['/api/users/me'] });
      }
    }
  }, [subscription?.tier, confirmingUpgrade, upgradePending, queryClient, upgradeReturn]);

  // Sync displayName once the async user data resolves
  useEffect(() => {
    if (user?.displayName) {
      setDisplayName(user.displayName);
    }
  }, [user?.displayName]);

  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');

  const handleUpdateProfile = (e: React.FormEvent) => {
    e.preventDefault();
    updateMe.mutate(
      { data: { displayName } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['/api/users/me'] });
        },
      }
    );
  };

  const handleConnectKalshi = (e: React.FormEvent) => {
    e.preventDefault();
    connectIntegration.mutate(
      {
        data: {
          platform: 'kalshi',
          apiKey,
          apiSecret: apiSecret || undefined,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['/api/integrations'] });
          setApiKey('');
          setApiSecret('');
        },
      }
    );
  };

  const handleDisconnect = (id: number) => {
    if (confirm('Are you sure you want to disconnect this integration?')) {
      deleteIntegration.mutate(
        { id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['/api/integrations'] });
          },
        }
      );
    }
  };

  const [upgradingTier, setUpgradingTier] = useState<'starter' | 'pro' | null>(null);

  const handleUpgrade = async (targetTier: 'starter' | 'pro') => {
    setUpgradingTier(targetTier);
    setCheckoutLoading(true);
    setCheckoutError(null);
    trackEvent(FUNNEL_EVENTS.CHECKOUT_STARTED, { tier: targetTier, source: 'settings' });
    try {
      const checkoutRes = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ targetTier, utm: getStoredUtmParams() ?? undefined }),
      });
      if (!checkoutRes.ok) {
        const err = await checkoutRes.json().catch(() => ({}));
        throw new Error(err.error ?? 'Failed to start checkout. Please try again.');
      }
      const { url } = await checkoutRes.json();
      if (!url) throw new Error('No checkout URL returned.');

      window.location.href = url;
    } catch (err: any) {
      setCheckoutError(err.message ?? 'An unexpected error occurred.');
      setCheckoutLoading(false);
      setUpgradingTier(null);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setPortalError(null);
    try {
      const res = await fetch('/api/stripe/portal', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error ?? 'Failed to open billing portal. Please try again.');
      }
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch (err: any) {
      setPortalError(err.message ?? 'Failed to open billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const handleCancelPlan = async () => {
    setCancelLoading(true);
    setCancelError(null);
    try {
      const res = await fetch('/api/stripe/portal/cancel', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error ?? 'Failed to open cancellation flow. Please try again.');
      }
      const { url } = await res.json();
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No portal URL returned.');
      }
    } catch (err: any) {
      setCancelError(err.message ?? 'An unexpected error occurred.');
      setCancelLoading(false);
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referrals?.referralLink) return;
    try {
      await navigator.clipboard.writeText(referrals.referralLink);
      trackEvent(FUNNEL_EVENTS.REFERRAL_LINK_COPIED, { source: 'settings' });
      toast({ title: 'Referral link copied!', description: 'Share it with a friend — you both earn free Pro days.' });
    } catch {
      toast({ title: 'Failed to copy link', variant: 'destructive' });
    }
  };

  const referralShareText = `I've been using MarketMind for AI-powered prediction market analysis — sign up with my link and we both get ${referrals?.rewardDaysPerReferral ?? 14} days of Pro free:`;

  const handleReferralShare = (channel: string) => {
    // External share URLs do not report whether the recipient completed sharing,
    // so this records invocation as the best available completion signal.
    trackEvent(FUNNEL_EVENTS.REFERRAL_LINK_SHARED, { channel });
  };

  // Entitlement/feature display must reflect effectiveTier (real Stripe tier OR
  // an active referral bonus) — otherwise a rewarded user would be shown as Free
  // and prompted to upgrade even though they already have unlimited analyses.
  // Billing-specific sections (renewal date, manage/cancel plan) intentionally
  // keep using the raw Stripe tier below, since those only apply to real subscribers.
  const effectiveTier = ((subscription as any)?.effectiveTier ?? subscription?.tier ?? 'free') as 'free' | 'starter' | 'pro';

  // Referral bonus expiry: prompt the user before their unlimited analyses
  // silently disappear. `bonusOnly` is true when Pro access comes ONLY from
  // the bonus (their real Stripe tier isn't already pro) — those users lose
  // everything when it lapses, so the warning is styled more urgently for them.
  const bonusProUntil = referrals?.bonusProUntil ?? null;
  const bonusOnly = bonusProUntil != null && subscription?.tier !== 'pro';
  const BONUS_WARNING_WINDOW_DAYS = 3;
  const daysUntilBonusExpiry = bonusProUntil
    ? Math.max(0, Math.ceil((new Date(bonusProUntil).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;
  const bonusExpiringSoon = daysUntilBonusExpiry !== null && daysUntilBonusExpiry <= BONUS_WARNING_WINDOW_DAYS;

  const usagePercent =
    subscription && subscription.analysesLimit > 0
      ? (subscription.analysesUsed / subscription.analysesLimit) * 100
      : 0;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6 pb-24 lg:pb-8">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and preferences
        </p>
      </div>

      {/* Pro access lapsed banner
          `isLapsed` reflects the raw Stripe subscription only (it fires for past_due,
          unpaid, or a fully canceled/deleted subscription). A user can still be covered
          by an active referral bonus (effectiveTier === 'pro') even while their real
          subscription is gone — in that case the copy must say so, or it wrongly
          implies they've already lost unlimited access. Copy and the recommended
          action both vary by whether the subscription can still be recovered
          (past_due/unpaid, via the billing portal) or is fully gone (canceled/
          incomplete_expired/unknown, which needs a fresh subscription instead). */}
      {(subscription as any)?.isLapsed && (() => {
        const stripeStatus = (subscription as any).stripeStatus as string | null;
        const isRecoverable = stripeStatus === 'past_due' || stripeStatus === 'unpaid';
        const bonusProUntil = (subscription as any).bonusProUntil as string | null;
        const bonusCoverage = effectiveTier === 'pro' && bonusProUntil ? (
          <>
            {' '}You still have unlimited analyses from your active referral bonus through{' '}
            <span className="font-medium">
              {new Date(bonusProUntil).toLocaleDateString(undefined, {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            </span>
            .
          </>
        ) : effectiveTier === 'pro' ? (
          ' You still have unlimited analyses from your active referral bonus.'
        ) : null;

        return (
          <div className="flex flex-col gap-2 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold">
                  {isRecoverable ? 'Your subscription payment failed' : 'Your subscription has ended'}
                </p>
                <p className="text-sm mt-0.5">
                  {isRecoverable
                    ? `Your subscription is ${stripeStatus === 'past_due' ? 'past due' : 'unpaid'}.`
                    : 'Your Pro subscription was canceled.'}
                  {bonusCoverage}
                  {isRecoverable ? (
                    <>{' '}{bonusCoverage ? 'Update your payment method to keep Pro after that.' : 'Update your payment method to restore unlimited analyses.'}</>
                  ) : (
                    <>{' '}{bonusCoverage ? 'Resubscribe to keep Pro after that.' : 'Resubscribe to restore unlimited analyses.'}</>
                  )}
                </p>
              </div>
              <Button size="sm" variant="outline" className="shrink-0 border-amber-300 hover:bg-amber-100" onClick={handleManageBilling} disabled={portalLoading}>
                {portalLoading ? <Spinner size="sm" /> : 'Update billing'}
              </Button>
            </div>
            {/* The card-level portalError below only renders for pro/starter users (subscription.tier),
                but a lapsed user's raw tier is 'free' — so surface the error here too, or it would be
                set with nowhere to display. */}
            {portalError && (
              <div className="flex items-center justify-between gap-2 pl-8 text-sm text-destructive">
                <span>{portalError}</span>
                <button
                  type="button"
                  aria-label="Dismiss error"
                  className="shrink-0 opacity-70 hover:opacity-100"
                  onClick={() => setPortalError(null)}
                >
                  ×
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Referral bonus expiring-soon banner — the prominent, page-top warning
          for a bonus nearing expiry. `bonusOnly` users lose unlimited analyses
          entirely once it lapses (their real Stripe tier isn't pro), so their
          copy and CTA are more direct than a real Pro subscriber's. */}
      {bonusExpiringSoon && bonusProUntil && (
        <div
          className={`flex flex-col gap-3 p-4 rounded-lg border ${
            bonusOnly
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold">
                {daysUntilBonusExpiry === 0
                  ? 'Your referral Pro bonus expires today'
                  : `Your referral Pro bonus expires in ${daysUntilBonusExpiry} day${daysUntilBonusExpiry === 1 ? '' : 's'}`}
              </p>
              <p className="text-sm mt-0.5">
                {bonusOnly
                  ? `You'll lose unlimited analyses on ${new Date(bonusProUntil).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} unless you subscribe or refer another friend.`
                  : `Your bonus ends on ${new Date(bonusProUntil).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} — no impact for you since you're already subscribed to Pro.`}
              </p>
            </div>
          </div>
          {bonusOnly && (
            <div className="flex flex-wrap gap-2 pl-8">
              <Button
                size="sm"
                onClick={() => handleUpgrade('pro')}
                disabled={checkoutLoading}
              >
                {checkoutLoading && upgradingTier === 'pro' ? (
                  <><Spinner size="sm" className="mr-2" />Opening Checkout...</>
                ) : (
                  <><Zap className="mr-2 h-4 w-4" />Subscribe to Pro</>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 hover:bg-red-100"
                onClick={handleCopyReferralLink}
                disabled={!referrals?.referralLink}
              >
                Refer a friend
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Upgrade confirming banner (webhook not yet received) */}
      {confirmingUpgrade && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200 text-blue-800">
          <Spinner size="sm" className="flex-shrink-0" />
          <div>
            <p className="font-semibold">Confirming your upgrade…</p>
            <p className="text-sm">Hang tight while we activate your Pro plan.</p>
          </div>
        </div>
      )}

      {/* Upgrade still-pending banner (timeout expired before webhook arrived) */}
      {upgradePending && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Still confirming your upgrade…</p>
            <p className="text-sm mt-0.5">
              Your payment was received but we haven't heard back from our payment provider yet.
              Check back in a minute or{' '}
              <button
                className="underline font-medium"
                onClick={() => window.location.reload()}
              >
                refresh the page
              </button>
              .
            </p>
          </div>
        </div>
      )}

      {/* Upgrade success banner */}
      {upgradeConfirmed && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Welcome to Pro! 🎉</p>
            <p className="text-sm">You now have unlimited AI analyses.</p>
          </div>
        </div>
      )}

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="h-5 w-5" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={clerkUser?.imageUrl} />
              <AvatarFallback>
                {clerkUser?.firstName?.[0]}
                {clerkUser?.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">
                {clerkUser?.firstName} {clerkUser?.lastName}
              </p>
              <p className="text-sm text-muted-foreground">
                {clerkUser?.primaryEmailAddress?.emailAddress}
              </p>
            </div>
          </div>

          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={clerkUser?.primaryEmailAddress?.emailAddress || ''}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Managed by Clerk authentication
              </p>
            </div>

            <Button type="submit" disabled={updateMe.isPending}>
              {updateMe.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Analysis complete emails</p>
              <p className="text-sm text-muted-foreground">
                Get an email when your AI analysis finishes
              </p>
            </div>
            {emailNotifEnabled === undefined ? (
              <div className="h-6 w-11 rounded-full bg-muted animate-pulse" />
            ) : (
            <Switch
              checked={emailNotifEnabled}
              disabled={emailNotifLoading}
              onCheckedChange={async (enabled) => {
                setEmailNotifEnabled(enabled);
                setEmailNotifLoading(true);
                try {
                  const res = await fetch('/api/users/me', {
                    method: 'PATCH',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ emailNotificationsEnabled: enabled }),
                  });
                  if (!res.ok) throw new Error('Failed to update notification preference');
                  trackEvent(FUNNEL_EVENTS.NOTIFICATIONS_TOGGLED, {
                    enabled,
                    channel: 'email',
                  });
                } catch {
                  setEmailNotifEnabled(!enabled);
                } finally {
                  setEmailNotifLoading(false);
                }
              }}
            />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Refer a friend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Refer a friend
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Share your link. When a friend signs up and runs their first analysis, you both get{' '}
            <span className="font-medium text-foreground">{referrals?.rewardDaysPerReferral ?? 14} days of Pro</span> — free.
          </p>

          {referrals?.bonusProUntil && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span>
                Referral bonus active — Pro access through{' '}
                <span className="font-medium">
                  {new Date(referrals.bonusProUntil).toLocaleDateString(undefined, {
                    year: 'numeric', month: 'long', day: 'numeric',
                  })}
                </span>
              </span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={referrals?.referralLink ?? ''} readOnly className="bg-muted font-mono text-sm" />
            <Button onClick={handleCopyReferralLink} disabled={!referrals?.referralLink} className="shrink-0">
              Copy link
            </Button>
          </div>

          <div className="flex gap-3 text-sm">
            <a
              href={referrals?.referralLink ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(referralShareText)}&url=${encodeURIComponent(referrals.referralLink)}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className={`hover:text-sky-500 transition-colors ${!referrals?.referralLink ? 'pointer-events-none opacity-50' : ''}`}
              onClick={() => handleReferralShare('x')}
            >
              Share on X
            </a>
            <span className="text-muted-foreground">·</span>
            <a
              href={referrals?.referralLink ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referrals.referralLink)}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className={`hover:text-blue-600 transition-colors ${!referrals?.referralLink ? 'pointer-events-none opacity-50' : ''}`}
              onClick={() => handleReferralShare('linkedin')}
            >
              Share on LinkedIn
            </a>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-border text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>
              <span className="font-medium text-foreground">{referrals?.totalReferred ?? 0}</span> friends referred ·{' '}
              <span className="font-medium text-foreground">{referrals?.totalRewarded ?? 0}</span> rewards earned
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Current Plan</p>
              <p className="text-sm text-muted-foreground">
                {effectiveTier === 'pro' ? 'Professional' : effectiveTier === 'starter' ? 'Starter' : 'Free'}
              </p>
            </div>
            <TierBadge tier={effectiveTier} />
          </div>

          {subscription && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Analyses Used</span>
                <span className="font-medium">
                  {subscription.analysesUsed} of{' '}
                  {effectiveTier === 'pro' ? 'Unlimited' : subscription.analysesLimit}
                </span>
              </div>
              {effectiveTier !== 'pro' && (
                <>
                  <Progress value={usagePercent} />
                  {subscription.resetDate && (
                    <p className="text-xs text-muted-foreground">
                      Resets {new Date(subscription.resetDate).toLocaleDateString()}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {checkoutError && (
            <p className="text-sm text-destructive">{checkoutError}</p>
          )}

          {effectiveTier === 'pro' ? null : (
            <div className="space-y-3">
              {/* Show Starter option only for free users */}
              {effectiveTier !== 'starter' && (
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm">Starter — $12/month</p>
                    <span className="text-xs text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">25 analyses/mo</span>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-0.5">
                    <li>✓ AI follow-up chat on analyses</li>
                    <li>✓ Email notifications</li>
                  </ul>
                  <Button
                    variant="outline"
                    className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => handleUpgrade('starter')}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading && upgradingTier === 'starter' ? (
                      <><Spinner size="sm" className="mr-2" />Opening Checkout...</>
                    ) : (
                      'Upgrade to Starter'
                    )}
                  </Button>
                </div>
              )}

              {/* Pro always shown for free and starter users */}
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/10 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm">Pro — $29/month</p>
                  <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full">Unlimited</span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-0.5">
                  <li>✓ Unlimited AI analyses</li>
                  <li>✓ Priority analysis queue</li>
                  <li>✓ Early access to new features</li>
                </ul>
                <Button
                  className="w-full"
                  onClick={() => handleUpgrade('pro')}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading && upgradingTier === 'pro' ? (
                    <><Spinner size="sm" className="mr-2" />Opening Checkout...</>
                  ) : (
                    <><Zap className="mr-2 h-4 w-4" />Upgrade to Pro</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {(subscription?.tier === 'pro' || subscription?.tier === 'starter') && (
            <div className="space-y-3">
              {/* Renewal / cancellation info.
                  cancelAtPeriodEnd must surface its own notice even when renewalDate is
                  still null (e.g. before the one-time backfill or a webhook has populated
                  it) — otherwise a cancelled subscriber sees no indication at all. */}
              {((subscription as any).renewalDate || (subscription as any).cancelAtPeriodEnd) && (
                <div className="text-sm text-muted-foreground">
                  {(subscription as any).cancelAtPeriodEnd ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>
                        <span className="font-medium">Cancellation scheduled</span>
                        {(subscription as any).renewalDate ? (
                          <>
                            {' '}— your plan ends on{' '}
                            {new Date((subscription as any).renewalDate).toLocaleDateString(undefined, {
                              year: 'numeric', month: 'long', day: 'numeric',
                            })}
                          </>
                        ) : (
                          <> — your plan will end at the close of the current billing period.</>
                        )}
                      </span>
                    </div>
                  ) : (
                    (subscription as any).renewalDate && (
                      <p>
                        Renews on{' '}
                        <span className="font-medium text-foreground">
                          {new Date((subscription as any).renewalDate).toLocaleDateString(undefined, {
                            year: 'numeric', month: 'long', day: 'numeric',
                          })}
                        </span>
                      </p>
                    )
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleManageBilling}
                  disabled={portalLoading || cancelLoading}
                >
                  {portalLoading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Opening...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Manage Billing
                    </>
                  )}
                </Button>

                {!(subscription as any).cancelAtPeriodEnd && (
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={handleCancelPlan}
                    disabled={cancelLoading || portalLoading}
                  >
                    {cancelLoading ? (
                      <Spinner size="sm" />
                    ) : (
                      'Cancel plan'
                    )}
                  </Button>
                )}
              </div>
              {portalError && (
                <div className="flex items-center justify-between gap-2 text-sm text-destructive">
                  <span>{portalError}</span>
                  <button
                    type="button"
                    aria-label="Dismiss error"
                    className="shrink-0 opacity-70 hover:opacity-100"
                    onClick={() => setPortalError(null)}
                  >
                    ×
                  </button>
                </div>
              )}
              {cancelError && (
                <div className="flex items-center justify-between gap-2 text-sm text-destructive">
                  <span>{cancelError}</span>
                  <button
                    type="button"
                    aria-label="Dismiss error"
                    className="shrink-0 opacity-70 hover:opacity-100"
                    onClick={() => setCancelError(null)}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Integrations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Integrations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Connected Accounts */}
          {integrations && integrations.integrations.length > 0 && (
            <div className="space-y-3">
              <Label>Connected Accounts</Label>
              {integrations.integrations.map((integration) => (
                <div
                  key={integration.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border"
                >
                  <div>
                    <p className="font-medium capitalize">{integration.platform}</p>
                    <p className="text-sm text-muted-foreground">
                      ••••••••{integration.maskedApiKey.slice(-4)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDisconnect(integration.id)}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Connect Kalshi */}
          <div className="space-y-4">
            <Label>Connect Kalshi</Label>
            <form onSubmit={handleConnectKalshi} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="apiKey">API Key</Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Your Kalshi API key"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="apiSecret">API Secret (optional)</Label>
                <Input
                  id="apiSecret"
                  type="password"
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="Your Kalshi API secret"
                />
              </div>

              <Button type="submit" disabled={connectIntegration.isPending}>
                {connectIntegration.isPending ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Connect Kalshi
                  </>
                )}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
